import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const navItems = [
  { path: '/dashboard',  icon: '◈',  label: 'Dashboard',    roles: ['SUPER_ADMIN','CHINA_STAFF','BD_STAFF'] },
  { path: '/customers',  icon: '◉',  label: 'Customers',    roles: ['SUPER_ADMIN','CHINA_STAFF'] },
  { path: '/products',   icon: '⊹',  label: 'Products',     roles: ['SUPER_ADMIN','CHINA_STAFF'] },
  { path: '/warehouse',  icon: '▣',  label: 'Warehouse',    roles: ['SUPER_ADMIN','CHINA_STAFF','BD_STAFF'] },
  { path: '/bd-receive', icon: '🇧🇩', label: 'BD Receiving', roles: ['SUPER_ADMIN','BD_STAFF'] },
  { path: '/shipments',  icon: '⬡',  label: 'Shipments',    roles: ['SUPER_ADMIN','CHINA_STAFF','BD_STAFF'] },
  { path: '/tracking',   icon: '◎',  label: 'Tracking',     roles: ['SUPER_ADMIN','CHINA_STAFF','BD_STAFF','CUSTOMER'] },
  { path: '/reports',    icon: '▦',  label: 'Reports',      roles: ['SUPER_ADMIN','CHINA_STAFF','BD_STAFF'] },
];

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };
  const visible = navItems.filter(n => n.roles.includes(user?.role));

  return (
    <aside style={{
      width: collapsed ? 64 : 'var(--sidebar-width)',
      background: 'var(--bg-secondary)',
      borderRight: '1px solid var(--border)',
      height: '100vh',
      position: 'fixed',
      left: 0, top: 0,
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.25s ease',
      overflow: 'hidden',
      zIndex: 100,
    }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '20px 16px' : '24px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12,
        minHeight: 72,
      }}>
        <div style={{
          width: 36, height: 36,
          background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
          borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, flexShrink: 0,
          boxShadow: '0 0 20px var(--accent-glow)',
        }}>⬡</div>
        {!collapsed && (
          <div style={{ animation: 'fadeIn 0.2s ease' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', lineHeight: 1 }}>Logistics</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>China–Bangladesh Logistics Support</div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, padding: 4, borderRadius: 6, transition: 'color 0.2s', flexShrink: 0 }}
          title={collapsed ? 'Expand' : 'Collapse'}
        >{collapsed ? '›' : '‹'}</button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
        {visible.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : ''}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 12,
              padding: collapsed ? '12px 16px' : '11px 14px',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 4,
              color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
              background: isActive ? 'rgba(59,130,246,0.12)' : 'transparent',
              borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent',
              fontWeight: isActive ? 600 : 400,
              fontSize: 14,
              transition: 'all 0.15s',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            })}
          >
            <span style={{ fontSize: 18, flexShrink: 0, width: 20, textAlign: 'center' }}>{item.icon}</span>
            {!collapsed && <span style={{ animation: 'fadeIn 0.2s ease' }}>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div style={{ padding: collapsed ? '12px 8px' : '16px 12px', borderTop: '1px solid var(--border)' }}>
        {!collapsed && (
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 8, animation: 'fadeIn 0.2s ease' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
            <div style={{ marginTop: 6 }}>
              <span className={`badge ${user?.role === 'SUPER_ADMIN' ? 'badge-red' : user?.role === 'CHINA_STAFF' ? 'badge-blue' : user?.role === 'BD_STAFF' ? 'badge-green' : 'badge-purple'}`} style={{ fontSize: 10 }}>
                {user?.role}
              </span>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 8, padding: collapsed ? '10px' : '10px 14px',
            background: 'none', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
            cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
          title="Logout"
        >
          <span>⎋</span>
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
